import React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { useCpsDialog } from '../../../common/useCpsDialog';
import { Upload, UploadListItemProps } from '@progress/kendo-react-upload';
import { SvgIcon } from '@progress/kendo-react-common';
import { trashIcon } from '@progress/kendo-svg-icons';
import { useTranslationCsvUpload } from './useTranslationCsvUpload';
import { buildTranslationCsvEndpointPath } from '@cs-cms/api-client';
import { useGlobalizationContext, useTranslation, useNotificationContext } from '@cs-cms/state-management';
import { useUIConfig } from '../../../UiProvider';

interface StaticTranslationCsvManagerProps {
  activeLocale?: { id: number; name: string };
}

const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export const StaticTranslationCsvManager: React.FC<StaticTranslationCsvManagerProps> = ({ activeLocale }) => {
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { showDialog, DialogComponent } = useCpsDialog();
  const { showToast } = useNotificationContext();
  const { apiUrl } = useUIConfig();

  const {
    uploadedFiles,
    changedTranslations,
    hasChanges,
    isProcessing,
    validationError,
    uploadTranslationsMutation,
    onAddHandler,
    onRemoveHandler,
    removeFile,
    clearUpload,
    handleSave
  } = useTranslationCsvUpload({ activeLocale, translate });

  const handleExportCsv = async () => {
    if (!activeLocale) {
      showToast({
        message: translate('custom.settings.adminCenter.translationManagement.export.noLocaleError'),
        type: 'error'
      });
      return;
    }

    try {
      const csvUrl = `${apiUrl}/${buildTranslationCsvEndpointPath(activeLocale.name)}`;
      window.open(csvUrl, '_blank');

      showToast({
        message: translate('custom.settings.adminCenter.translationManagement.export.success'),
        type: 'success'
      });
    } catch {
      showToast({
        message: translate('custom.settings.adminCenter.translationManagement.export.error'),
        type: 'error'
      });
    }
  };

  const customItemRenderer = (props: UploadListItemProps): React.JSX.Element => {
    return (
      <>
        {props.files.map((file, index) => (
          <div key={file.uid || index} className="k-file">
            <div className="attachmentItem k-file-single">
              <div className="attachmentName">{file.name}</div>
              <div className="attachmentActionButtons">
                <button
                  type="button"
                  className="k-button k-button-md k-button-flat k-button-flat-primary"
                  onClick={() => removeFile(file)}
                  title="Remove file"
                >
                  <SvgIcon icon={trashIcon} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </>
    );
  };

  const handleSaveClick = () => {
    showDialog({
      title: translate('custom.settings.adminCenter.translationManagement.save.confirmTitle'),
      message: translate('custom.settings.adminCenter.translationManagement.save.confirmationMessage'),
      onConfirm: async confirmed => {
        if (confirmed) {
          await handleSave();
        }
      },
      confirmButtonLabel: translate('custom.common.save'),
      discardButtonLabel: translate('custom.common.cancel')
    });
  };

  return (
    <>
      <>
        <h3>{translate('custom.settings.adminCenter.translationManagement.csvWorkflow.title')}</h3>
        <ol>
          <li>{translate('custom.settings.adminCenter.translationManagement.csvWorkflow.step1')}</li>
          <li>{translate('custom.settings.adminCenter.translationManagement.csvWorkflow.step2')}</li>
          <li>{translate('custom.settings.adminCenter.translationManagement.csvWorkflow.step3')}</li>
          <li>{translate('custom.settings.adminCenter.translationManagement.csvWorkflow.step4')}</li>
          <li>{translate('custom.settings.adminCenter.translationManagement.csvWorkflow.step5')}</li>
          <li>{translate('custom.settings.adminCenter.translationManagement.csvWorkflow.step6')}</li>
        </ol>
      </>

      <>
        <Button
          onClick={handleExportCsv}
          themeColor="primary"
          disabled={!activeLocale}
          className="staticTranslationsBtn"
        >
          {translate('custom.settings.adminCenter.translationManagement.export.button')}
        </Button>
      </>

      <>
        <h4>{translate('custom.settings.adminCenter.translationManagement.upload.title')}</h4>
        <Upload
          autoUpload={false}
          showActionButtons={false}
          showFileList={true}
          listItemUI={customItemRenderer}
          files={uploadedFiles}
          onAdd={onAddHandler}
          onRemove={onRemoveHandler}
          disabled={isProcessing || !activeLocale}
          className="csvUpload"
          multiple={false}
          restrictions={{
            maxFileSize: MAX_FILE_SIZE_BYTES,
            allowedExtensions: ['.csv']
          }}
        />
        {validationError && (
          <div className="k-form-error" role="alert">
            {validationError}
          </div>
        )}
        {uploadedFiles.length > 0 && (
          <Button onClick={clearUpload} fillMode="flat" size="small">
            {translate('custom.settings.adminCenter.translationManagement.upload.clearButton')}
          </Button>
        )}
        {isProcessing && <>{translate('custom.settings.adminCenter.translationManagement.upload.processing')}</>}
      </>

      {hasChanges && changedTranslations.length > 0 && (
        <>
          <h4>{translate('custom.settings.adminCenter.translationManagement.changes.title')}</h4>
          <>
            <Grid data={changedTranslations}>
              <GridColumn
                field="technicalName"
                title={translate('custom.settings.adminCenter.translationManagement.changes.columnTechnical')}
                width="300px"
              />
              <GridColumn
                field="reference"
                title={translate('custom.settings.adminCenter.translationManagement.reference')}
                width="250px"
              />
              <GridColumn
                field="translation"
                title={translate('custom.settings.adminCenter.translationManagement.translation')}
              />
            </Grid>
          </>

          <>
            <div>
              {changedTranslations.length}{' '}
              {changedTranslations.length === 1
                ? translate('custom.settings.adminCenter.translationManagement.changes.countSingular')
                : translate('custom.settings.adminCenter.translationManagement.changes.countPlural')}
            </div>
            <Button
              onClick={handleSaveClick}
              themeColor="primary"
              disabled={uploadTranslationsMutation.isPending}
              className="staticTranslationsBtn"
            >
              {uploadTranslationsMutation.isPending
                ? translate('custom.settings.adminCenter.translationManagement.save.saving')
                : translate('custom.settings.adminCenter.translationManagement.save.button')}
            </Button>
          </>
        </>
      )}

      {uploadedFiles.length > 0 && !hasChanges && !isProcessing && (
        <>{translate('custom.settings.adminCenter.translationManagement.changes.noChanges')}</>
      )}

      {DialogComponent}
    </>
  );
};
